import StorageService from '@/services/StorageService';
import gql from 'graphql-tag';
import { apolloClient } from '@/apollo';
import sessionTimer from './sessionTimer';
import router from '../router/Router';

const ACCESSED_ROUTE_KEY = 'accessed-route';
const ACCESS_TOKEN_EXPIRATION_THRESHOLD_MS = 60000;
const IS_TOKEN_REFRESHING_KEY = 'token-refreshing';
const API_BASE_PATH = process.env.VUE_APP_API_BASE_PATH;

const loggedUser = () => JSON.parse(StorageService.get('loggedUser'));

const addUserToStorage = async () => {
  try {
    const result = await apolloClient.query({
      fetchPolicy: 'network-only',
      query: gql`
        query User {
          getUserInfo {
            email
            admin
            displayName
            id
            tokenExpiresAt
          }
        }
      `,
    });

    StorageService.add('loggedUser', JSON.stringify(result.data.getUserInfo));
  } catch (e) {
    throw e;
  }
};

const setTokenRefreshing = (isRefreshing) => {
  if (isRefreshing) {
    StorageService.add(IS_TOKEN_REFRESHING_KEY, 'true');
  } else {
    StorageService.remove(IS_TOKEN_REFRESHING_KEY);
  }
};

const isTokenRefreshing = () => StorageService.get(IS_TOKEN_REFRESHING_KEY) === 'true';

const login = () => {
  const route = window.location.pathname.replace(API_BASE_PATH, '');
  const params = window.location.search;
  if (route) {
    StorageService.add(ACCESSED_ROUTE_KEY, route + params);
  }
  window.open(`${API_BASE_PATH}/auth/login`, '_self');
};

const startSessionTimer = async () => {
  await addUserToStorage();

  sessionTimer.start(Number(loggedUser().tokenExpiresAt) - ACCESS_TOKEN_EXPIRATION_THRESHOLD_MS, async () => {
    if (!isTokenRefreshing()) {
      await refreshTokens(); // eslint-disable-line no-use-before-define
    }
  });
};

const logout = () => {
  StorageService.remove('loggedUser');
  window.open(`${API_BASE_PATH}/auth/logout`, '_self');
};

const refreshTokens = async () => {
  try {
    setTokenRefreshing(true);

    await fetch(`${API_BASE_PATH}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (e) {
    setTokenRefreshing(false);
    throw e;
  } finally {
    setTokenRefreshing(false);
    await startSessionTimer();
  }
};

const handlePostLoginRedirect = () => {
  const route = StorageService.get(ACCESSED_ROUTE_KEY);
  if (route) {
    router.replace(route);
    StorageService.remove(ACCESSED_ROUTE_KEY);
  }
};

const isAdmin = () => {
  if (!loggedUser()) {
    return false;
  }

  return loggedUser().admin;
};

const silentLogin = async () => {
  if (!StorageService.get('loggedUser')) {
    login();
    await startSessionTimer();
    return;
  }

  try {
    await startSessionTimer();
    handlePostLoginRedirect();
  } catch (e) {
    try {
      await refreshTokens();
    } catch {
      login();
    }
  }
};

export default {
  startSessionTimer,
  refreshTokens,
  loggedUser,
  silentLogin,
  logout,
  isAdmin,
};
